// Footer (RESTYLE §6): a thin mono status bar under a hairline.
//   left   "● api ok · 3 nodes · 4/8 slots free · ● 2 live · 1 overdue" (+ the account line on phones)
//          api: ok (benign dot) | unreachable (hollow ink ring, ink) while /v1/nodes gets no answer or a 5xx;
//          nodes and slots are plain text; "N live · M overdue" (useLiveRuns, one rule with the Analyses chip and
//          lede) links to the Analyses "Live" filter, which lists exactly N + M runs
//   right  "[?] shortcuts · g a analyses · theme light · dark · system · malbox 0.1"
// The ? key opens the sheet; the three-way theme choice is the only place "system" is reachable.
// Under 720px the keyboard hints drop out.
//   <Footer onShowKeys flat? />   flat: an opaque paper band that continues the page's .flat band (Submit)
import { Link } from "react-router-dom";
import { Sep } from "@/components/ui";
import { useNodes, useTheme, type ThemePref } from "@/hooks";
import { isApiError } from "@/lib/api";
import { AccountLine } from "./Account";
import { useLiveRuns } from "./useLiveRuns";

const VERSION = __MALBOX_VERSION__.split(".").slice(0, 2).join(".");
const CHOICES: ThemePref[] = ["light", "dark", "system"];

function ThemeChoice() {
  const { pref, override, setPref } = useTheme();
  const current = override ?? pref;
  return (
    <span className="theme-choice" role="group" aria-label="Theme">
      {CHOICES.map((p) => (
        <button key={p} type="button" aria-pressed={current === p} onClick={() => setPref(p)}>
          {p}
        </button>
      ))}
    </span>
  );
}

function apiState(isError: boolean, err: unknown, ready: boolean): { cls: string; dot: string; word: string } {
  if (isError) {
    if (isApiError(err) && err.status < 500) return { cls: "sb sb--down", dot: "sb-dot down", word: `error ${err.status}` };
    return { cls: "sb sb--down", dot: "sb-dot down", word: "unreachable" };
  }
  return ready ? { cls: "sb", dot: "sb-dot ok", word: "ok" } : { cls: "sb sb--off", dot: "sb-dot", word: "…" };
}

function StatusBar() {
  const nodes = useNodes();
  const live = useLiveRuns();
  const list = nodes.data;
  const api = apiState(nodes.isError, nodes.error, !!list);
  const total = list?.reduce((s, n) => s + Math.max(0, n.max_slots), 0) ?? 0;
  const free = list?.reduce((s, n) => s + Math.min(Math.max(0, n.free_slots), Math.max(0, n.max_slots)), 0) ?? 0;
  return (
    <span className="statusbar" role="group" aria-label="Status">
      <span className={api.cls} title={`control plane ${window.location.host}`}>
        <i className={api.dot} aria-hidden="true" />
        api {api.word}
      </span>
      {list && (
        <>
          <Sep />
          <span className="sb">
            <b>{list.length}</b> {list.length === 1 ? "node" : "nodes"}
          </span>
          <Sep />
          <span className="sb">
            <b>
              {free}/{total}
            </b>{" "}
            {total === 1 ? "slot" : "slots"} free
          </span>
        </>
      )}
      {live.ready && (
        <>
          <Sep />
          <Link
            className={live.count || live.overdue ? "sb" : "sb sb--off"}
            to="/jobs?state=live"
            viewTransition
            title={live.overdue ? "Runs on a node; overdue ones are past their timeout and not collected yet" : undefined}
          >
            {live.count > 0 && <i className="sb-dot live" aria-hidden="true" />}
            <b>{live.count}</b> live
            {live.overdue > 0 && (
              <>
                <Sep />
                <b>{live.overdue}</b> overdue
              </>
            )}
          </Link>
        </>
      )}
    </span>
  );
}

export function Footer({ flat }: { flat?: boolean }) {
  return (
    // flow-root keeps the footer's top margin inside the paper band
    <div className={flat ? "flat flow-root" : undefined}>
      <footer className="foot">
        <div className="page foot-in">
          <span className="inline-flex flex-wrap items-center gap-x-3 gap-y-1.5">
            <StatusBar />
            <AccountLine />
          </span>
          <span className="foot-keys gap-x-0">
            <span className="inline-flex items-center gap-1">
              theme
              <ThemeChoice />
            </span>
            <Sep />
            <span className="text-ink-faint">malbox {VERSION}</span>
          </span>
        </div>
      </footer>
    </div>
  );
}
